/**
 * Every Firestore path in the application, named once.
 *
 * Collection names are the one thing that is genuinely expensive to get wrong
 * — a typo in a string literal three screens deep fails silently as an empty
 * list — so no other file spells one out. Security Rules are written against
 * exactly these paths; changing one here means changing firestore.rules in the
 * same commit.
 */

import { collection, collectionGroup, doc, type DocumentReference } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { sanitizeFileName } from './fileNames';

export const COLLECTIONS = {
  users: 'users',
  inbox: 'inbox',
  semesters: 'semesters',
  enrollments: 'enrollments',
  courses: 'courses',
  announcements: 'announcements',
  todos: 'todos',
  documents: 'documents',
  documentCategories: 'documentCategories',
  agenda: 'agenda',
  seen: 'seen',
  notificationGroups: 'notificationGroups',
  pushSubscriptions: 'pushSubscriptions',
  deliveries: 'deliveries',
  notificationEvents: 'notificationEvents',
  studentActivity: 'studentActivity',
  /** Written by the previous version of the site. Read-only history. */
  legacyActivity: 'activity',
  legacyCourses: 'courses',
} as const;

// ── People ───────────────────────────────────────────────────────────────────

export const degreeEntriesCol = (uid: string) => collection(db, 'users', uid, 'degreeProgress');
export const degreeEntryDoc = (uid: string, id: string) => doc(db, 'users', uid, 'degreeProgress', id);

export const usersCol = () => collection(db, COLLECTIONS.users);
export const userDoc = (uid: string) => doc(db, COLLECTIONS.users, uid);
export const inboxCol = (uid: string) => collection(db, COLLECTIONS.users, uid, COLLECTIONS.inbox);
export const inboxDoc = (uid: string, itemId: string) =>
  doc(db, COLLECTIONS.users, uid, COLLECTIONS.inbox, itemId);

// ── Semesters ────────────────────────────────────────────────────────────────

export const semestersCol = () => collection(db, COLLECTIONS.semesters);
export const semesterDoc = (semesterId: string) => doc(db, COLLECTIONS.semesters, semesterId);

const sub = (semesterId: string, name: string) =>
  collection(db, COLLECTIONS.semesters, semesterId, name);

export const enrollmentsCol = (semesterId: string) => sub(semesterId, COLLECTIONS.enrollments);
export const enrollmentDoc = (semesterId: string, uid: string) =>
  doc(db, COLLECTIONS.semesters, semesterId, COLLECTIONS.enrollments, uid);

/**
 * Every enrollment across every semester.
 *
 * The student home screen starts here: rather than reading the semester list
 * (which it is not allowed to do) and testing each one, it asks which
 * semesters it belongs to and then fetches only those. One query instead of N,
 * and it works within the rules rather than around them.
 *
 * This is a collection-group query on a single field, which Firestore does
 * *not* index automatically — the `enrollments.uid` fieldOverride in
 * firestore.indexes.json is what makes it work, and without it this fails at
 * runtime with failed-precondition rather than at deploy.
 */
export const allEnrollments = () => collectionGroup(db, COLLECTIONS.enrollments);

export const coursesCol = (semesterId: string) => sub(semesterId, COLLECTIONS.courses);
export const courseDoc = (semesterId: string, courseId: string) =>
  doc(db, COLLECTIONS.semesters, semesterId, COLLECTIONS.courses, courseId);

export const announcementsCol = (semesterId: string) => sub(semesterId, COLLECTIONS.announcements);
export const announcementDoc = (semesterId: string, id: string) =>
  doc(db, COLLECTIONS.semesters, semesterId, COLLECTIONS.announcements, id);

export const todosCol = (semesterId: string) => sub(semesterId, COLLECTIONS.todos);
export const todoDoc = (semesterId: string, id: string) =>
  doc(db, COLLECTIONS.semesters, semesterId, COLLECTIONS.todos, id);

export const documentsCol = (semesterId: string) => sub(semesterId, COLLECTIONS.documents);
export const documentDoc = (semesterId: string, id: string) =>
  doc(db, COLLECTIONS.semesters, semesterId, COLLECTIONS.documents, id);

export const documentCategoriesCol = (semesterId: string) =>
  sub(semesterId, COLLECTIONS.documentCategories);
export const documentCategoryDoc = (semesterId: string, id: string) =>
  doc(db, COLLECTIONS.semesters, semesterId, COLLECTIONS.documentCategories, id);

export const agendaCol = (semesterId: string) => sub(semesterId, COLLECTIONS.agenda);
export const agendaDoc = (semesterId: string, id: string) =>
  doc(db, COLLECTIONS.semesters, semesterId, COLLECTIONS.agenda, id);

export const seenCol = (semesterId: string) => sub(semesterId, COLLECTIONS.seen);

/**
 * The id of a seen record.
 *
 * Deterministic so that recording a view is an idempotent upsert rather than
 * an ever-growing log — and so the Security Rules can verify that a student is
 * writing their own record by checking the id itself.
 */
export const seenId = (uid: string, contentType: string, contentId: string) =>
  `${uid}_${contentType}_${contentId}`;

export const seenDoc = (semesterId: string, uid: string, contentType: string, contentId: string) =>
  doc(
    db,
    COLLECTIONS.semesters,
    semesterId,
    COLLECTIONS.seen,
    seenId(uid, contentType, contentId)
  );

// ── Notifications ────────────────────────────────────────────────────────────

export const professorsCol = () => collection(db, 'professors');

export const notificationGroupsCol = () => collection(db, COLLECTIONS.notificationGroups);
export const notificationGroupDoc = (id: string) => doc(db, COLLECTIONS.notificationGroups, id);

export const pushSubscriptionsCol = () => collection(db, COLLECTIONS.pushSubscriptions);
export const pushSubscriptionDoc = (id: string) => doc(db, COLLECTIONS.pushSubscriptions, id);

export const deliveriesCol = () => collection(db, COLLECTIONS.deliveries);
export const deliveryDoc = (id: string) => doc(db, COLLECTIONS.deliveries, id);

export const notificationEventsCol = () => collection(db, COLLECTIONS.notificationEvents);
export const studentActivityCol = () => collection(db, COLLECTIONS.studentActivity);

// ── Storage object paths ─────────────────────────────────────────────────────

/**
 * Where uploads live.
 *
 * The shape is load-bearing: storage.rules reads the semester id and the
 * document id straight out of the path so it can check enrollment and
 * publication without a listing. Keep them positional.
 */
export const storagePaths = {
  document: (semesterId: string, documentId: string, fileName: string) =>
    `semesters/${semesterId}/documents/${documentId}/${sanitizeFileName(fileName)}`,
  announcementImage: (semesterId: string, announcementId: string, fileName: string) =>
    `semesters/${semesterId}/announcements/${announcementId}/${sanitizeFileName(fileName)}`,
  todoAttachment: (semesterId: string, todoId: string, fileName: string) =>
    `semesters/${semesterId}/todos/${todoId}/${sanitizeFileName(fileName)}`,
  agendaAttachment: (semesterId: string, eventId: string, fileName: string) =>
    `semesters/${semesterId}/agenda/${eventId}/${sanitizeFileName(fileName)}`,
  semesterCover: (semesterId: string, fileName: string) =>
    `semesters/${semesterId}/cover/${sanitizeFileName(fileName)}`,
};

/**
 * Re-exported so that path builders and their filename rule stay together
 * at the call site. The rule itself lives in ./fileNames.ts, where its
 * regular expressions can be read without the surrounding path plumbing.
 */
export { sanitizeFileName };

/** Document references typed loosely enough for the generic hooks. */
export type AnyDoc = DocumentReference;
